// src/components/dashboard/LearningJourneyDashboard.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useAIAssessment } from '../ai/AIAssessmentProvider';
import { Target, Trophy, Brain, Play, ChevronRight, Star, Clock, Shield, AlertTriangle, CheckCircle, RefreshCw, BookOpen, TrendingUp } from 'lucide-react';

const LearningJourneyDashboard = ({ userId, onStartTopic, progress }) => {
  const { currentUser, learningData, getDashboardData } = useAuth();
  const { assessmentData, loadAssessmentData } = useAIAssessment();
  const [journeyData, setJourneyData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [curatedTopics, setCuratedTopics] = useState([]); 

  // Create default topics that are always available (regardless of assessment)
  const createDefaultTopics = () => {
    const baseTopics = [
      {
        id: 'phishing_social_engineering',
        topic: 'phishing_social_engineering',
        topic_name: 'Phishing & Social Engineering',
        description: 'Learn to identify and respond to phishing attempts and social engineering attacks',
        icon: '/assets/images/malware_1.jpg',
        scenario_count: 7,
        estimated_time: '25-30 minutes',
        difficulty: 'beginner',
        priority: 'high',
        progress_percentage: 0,
        completed_scenarios: 0,
        is_recommended: true
      },
      {
        id: 'password_security',
        topic: 'password_security',
        topic_name: 'Password Security & Authentication',
        description: 'Master password management and multi-factor authentication best practices',
        icon: '🔐',
        scenario_count: 6,
        estimated_time: '20-25 minutes',
        difficulty: 'beginner',
        priority: 'high',
        progress_percentage: 0,
        completed_scenarios: 0,
        is_recommended: true
      },
      {
        id: 'malware_ransomware',
        topic: 'malware_ransomware',
        topic_name: 'Malware & Ransomware',
        description: 'Understand malware threats and ransomware response procedures',
        icon: '🦠',
        scenario_count: 7,
        estimated_time: '30-35 minutes',
        difficulty: 'intermediate',
        priority: 'medium',
        progress_percentage: 0,
        completed_scenarios: 0,
        is_recommended: true
      },
      {
        id: 'network_security',
        topic: 'network_security',
        topic_name: 'Network Security',
        description: 'Learn about network threats and security best practices',
        icon: '🌐',
        scenario_count: 7,
        estimated_time: '30-35 minutes',
        difficulty: 'intermediate',
        priority: 'medium',
        progress_percentage: 0,
        completed_scenarios: 0,
        is_recommended: false
      },
      {
        id: 'data_protection',
        topic: 'data_protection',
        topic_name: 'Data Protection & Privacy',
        description: 'Understand data handling, privacy laws, and breach response',
        icon: '🛡️',
        scenario_count: 7,
        estimated_time: '25-30 minutes',
        difficulty: 'intermediate',
        priority: 'medium',
        progress_percentage: 0,
        completed_scenarios: 0,
        is_recommended: false
      },
      {
        id: 'mobile_security',
        topic: 'mobile_security',
        topic_name: 'Mobile Security',
        description: 'Secure mobile devices and applications effectively',
        icon: '📱',
        scenario_count: 7,
        estimated_time: '20-25 minutes',
        difficulty: 'beginner',
        priority: 'low',
        progress_percentage: 0,
        completed_scenarios: 0,
        is_recommended: false
      },
      {
        id: 'incident_response',
        topic: 'incident_response',
        topic_name: 'Incident Response & Recovery',
        description: 'Learn incident response procedures and recovery protocols',
        icon: '🚨',
        scenario_count: 7,
        estimated_time: '35-40 minutes',
        difficulty: 'advanced',
        priority: 'low',
        progress_percentage: 0,
        completed_scenarios: 0,
        is_recommended: false
      }
    ];

    return baseTopics;
  };

  // Fetch curated learning journey from API
  useEffect(() => {
    const fetchLearningJourney = async () => {
      if (!currentUser?.id) {
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      setError(null);

      try {
        // Always show default topics first (no assessment required)
        const defaultTopics = createDefaultTopics();
        setCuratedTopics(defaultTopics);

        // Get dashboard data (this is cached, no API call)
        const dashboardData = getDashboardData();
        
        // Try to enhance with assessment data if available
        if (dashboardData?.hasAssessment) {
          try {
            // Fetch enhanced learning journey from API
            const response = await fetch(`/api/ai/learning-journey/${currentUser.id}`, {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
                'Content-Type': 'application/json'
              }
            });
            
            if (response.ok) {
              const data = await response.json();
              if (data.success) {
                setJourneyData({
                  ...data.data,
                  hasAssessment: true
                });
                
                // Enhance topics with assessment data
                const enhancedTopics = createCuratedTopics(dashboardData, data.data);
                setCuratedTopics(enhancedTopics);
              }
            }
          } catch (apiError) {
            // console.log('Assessment data unavailable, using default topics:', apiError);
          }
        }

        // Set basic journey data for display
        setJourneyData(prev => prev || {
          hasAssessment: dashboardData?.hasAssessment || false,
          competency_level: dashboardData?.competencyLevel || 'beginner',
          learning_journey: dashboardData?.learningJourney || [],
          message: dashboardData?.hasAssessment 
            ? 'Personalized content based on your assessment' 
            : 'General cybersecurity learning content available'
        });

      } catch (err) {
        console.error('Error in learning journey setup:', err);
        // Always fall back to default topics
        const fallbackTopics = createDefaultTopics();
        setCuratedTopics(fallbackTopics);
        setJourneyData({
          hasAssessment: false,
          competency_level: 'beginner',
          learning_journey: [],
          message: 'Default learning content loaded'
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchLearningJourney();
  }, [currentUser?.id]);

  // Create curated topics based on user assessment and learning journey
  const createCuratedTopics = (dashboardData, apiData) => {
    const baseTopics = createDefaultTopics();
    
    // If no assessment data, return base topics
    if (!dashboardData?.hasAssessment || !apiData) {
      return baseTopics;
    }

    // Enhance with assessment data
    const learningJourney = dashboardData.learningJourney || apiData?.learning_journey || [];
    const weaknesses = dashboardData.weaknesses || apiData?.weaknesses || [];
    const competencyLevel = dashboardData.competencyLevel || apiData?.competency_level || 'beginner';

    return baseTopics.map((topic, index) => ({
      ...topic,
      difficulty: competencyLevel === 'beginner' ? 'beginner' : 
                 competencyLevel === 'advanced' ? 'intermediate' : 
                 index < 3 ? 'beginner' : index < 5 ? 'intermediate' : 'advanced',
      priority: weaknesses.includes(topic.topic) ? 'high' : (index < 3 ? 'medium' : 'low'),
      progress_percentage: 0,
      completed_scenarios: 0,
      is_recommended: weaknesses.includes(topic.topic) || index < 3
    }));
  };

  // Get personalization reason for a topic
  const getPersonalizationReason = (topic, weaknesses, journeyItem, progressData) => {
    if (weaknesses && weaknesses.includes(topic.topic)) {
      return 'Identified as area for improvement in your assessment';
    }
    if (journeyItem?.priority === 'high') {
      return 'High priority based on your role and industry';
    }
    if (progressData && progressData.completed > 0 && progressData.avg_score < 70) {
      return 'Previous attempts suggest additional practice needed';
    }
    if (topic.priority === 'high') {
      return 'Essential security knowledge for your profile';
    }
    return 'Part of your personalized learning journey';
  };

  // Enhanced handler function
  const handleTopicStart = (topic) => {
    if (!topic || !topic.topic) {
      console.error('Invalid topic:', topic);
      return;
    }
    
    // Store user competency level for the learning module
    if (journeyData?.competency_level) {
      localStorage.setItem('user_competency_level', journeyData.competency_level);
    }
    
    // Enhanced navigation with competency level
    const competencyParam = journeyData?.competency_level || 'beginner';
    
    // Call the original onStartTopic if it exists, or navigate directly
    if (onStartTopic) {
      // Pass enhanced topic data with competency info
      const enhancedTopic = {
        ...topic,
        userCompetencyLevel: competencyParam,
        personalizationReason: getPersonalizationReason(topic, journeyData?.weaknesses),
        recommendedFor: currentUser?.id
      };
      onStartTopic(enhancedTopic);
    } else {
      // Direct navigation with enhanced params
      window.location.href = `/learning/module/${topic.id}?topic=${topic.topic}&level=${competencyParam}`;
    }
  };

  // Get priority badge color
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'bg-red-900 text-red-200 border-red-700';
      case 'medium':
        return 'bg-yellow-900 text-yellow-200 border-yellow-700';
      case 'low':
        return 'bg-green-900 text-green-200 border-green-700';
      default:
        return 'bg-gray-900 text-gray-200 border-gray-700';
    }
  };

  // Get difficulty color
  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'beginner':
        return 'text-green-400';
      case 'intermediate':
        return 'text-yellow-400';
      case 'advanced':
        return 'text-red-400';
      default:
        return 'text-gray-400';
    }
  };

  // Render loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="text-red-400 text-center">
        {error}
      </div>
    );
  }

  // Always show content now - no assessment gate
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2">
        <h2 className="text-xl font-bold text-white">Your Learning Journey</h2>
        <div className="text-xs sm:text-sm text-gray-400 flex items-start sm:items-center space-x-2">
          <BookOpen className="w-4 h-4 text-blue-400" />
          <span>
            {journeyData?.hasAssessment 
              ? `Curated based on your assessment • ${journeyData.competency_level} level`
              : 'Essential cybersecurity topics • Start learning anytime'
            }
          </span>
        </div>
      </div>

      {/* Learning Path Summary */}
      {journeyData && (
        <div className={`mb-6 rounded-lg p-4 ${
          journeyData.hasAssessment 
            ? 'bg-blue-900/20 border border-blue-800' 
            : 'bg-gray-800/50 border border-gray-700'
        }`}>
          <div className="flex items-start space-x-3">
            {journeyData.hasAssessment ? (
              <Brain className="w-5 h-5 text-blue-400 mt-1 flex-shrink-0" />
            ) : (
              <BookOpen className="w-5 h-5 text-green-400 mt-1 flex-shrink-0" />
            )}
            <div className="flex-1">
              <h3 className={`font-semibold mb-1 ${
                journeyData.hasAssessment ? 'text-blue-300' : 'text-green-300'
              }`}>
                {journeyData.hasAssessment ? 'AI-Personalized Learning Path' : 'Comprehensive Learning Library'}
              </h3>
              <p className="text-sm text-gray-400">
                {journeyData.hasAssessment ? (
                  <>
                    Based on your <span className="text-blue-300 font-medium">{journeyData.competency_level}</span> competency level, 
                    we've curated <span className="text-blue-300 font-medium">{curatedTopics.filter(t => t.is_recommended).length}</span> priority topics. 
                    Each module dynamically adapts to your skill level with appropriate depth and complexity.
                  </>
                ) : (
                  <>
                    Explore comprehensive cybersecurity content across all major topics. 
                    <button 
                      onClick={() => window.location.href = '/assessment'}
                      className="text-blue-400 hover:text-blue-300 underline ml-1"
                    >
                      Take the assessment
                    </button> for personalized recommendations.
                  </>
                )}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Learning Topics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {curatedTopics.map((topic) => (
          <div
            key={topic.id}
            className="bg-gray-700 rounded-lg p-3 sm:p-4 hover:bg-gray-600 transition-all duration-200 border border-gray-600 hover:border-blue-500 group cursor-pointer relative overflow-hidden"
            onClick={() => handleTopicStart(topic)}
          >
            {/* AI Personalization Indicator */}
            {journeyData?.hasAssessment && (
              <div className="absolute top-0 right-0 bg-gradient-to-bl from-blue-600 to-transparent p-4 rounded-bl-2xl opacity-75">
                <Brain className="w-4 h-4 text-white" />
              </div>
            )}

            {/* Topic Header */}
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center space-x-3 min-w-0">
                <div className="text-2xl">{topic.icon}</div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-white group-hover:text-blue-300 transition-colors truncate">
                    {topic.topic_name}
                  </h3>
                  <div className="flex flex-wrap items-center gap-2 mt-1">
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      topic.difficulty === journeyData?.competency_level 
                        ? 'bg-green-900 text-green-200' 
                        : getDifficultyColor(topic.difficulty)
                    }`}>
                      {topic.difficulty === journeyData?.competency_level 
                        ? `Perfect for your ${topic.difficulty} level` 
                        : topic.difficulty
                      }
                    </span>
                    <span className="text-gray-500">•</span>
                    <span className="text-xs text-gray-400">
                      <Clock className="w-3 h-3 inline mr-1" />
                      {topic.estimated_time}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Priority Badge */}
              {topic.is_recommended && (
                <div className={`px-2 py-1 rounded text-xs border ${getPriorityColor(topic.priority)}`}>
                  {topic.priority === 'high' ? 
                    <AlertTriangle className="w-3 h-3 inline mr-1" /> : 
                    topic.priority === 'low' ? 
                    <CheckCircle className="w-3 h-3 inline mr-1" /> :
                    <Target className="w-3 h-3 inline mr-1" />
                  }
                  {topic.priority === 'high' ? 'Priority' : 
                   topic.priority === 'medium' ? 'Recommended' : 'Optional'}
                </div>
              )}
            </div>

            {/* Topic Description */}
            <p className="text-sm text-gray-300 mb-4 line-clamp-2">
              {topic.description}
            </p>

            {/* Progress Bar */}
            <div className="mb-3">
              <div className="flex justify-between text-xs text-gray-400 mb-1">
                <span>Progress</span>
                <span>{topic.progress_percentage}%</span>
              </div>
              <div className="w-full bg-gray-600 rounded-full h-1.5">
                <div 
                  className="bg-blue-500 h-1.5 rounded-full transition-all duration-300"
                  style={{ width: `${topic.progress_percentage}%` }}
                />
              </div>
            </div>

            {/* Action Area */}
            <div className="flex items-center justify-between gap-3">
              <div className="text-xs text-gray-400">
                {topic.completed_scenarios || 0} / {topic.scenario_count} scenarios
              </div>
              <div className="flex items-center space-x-1 text-blue-400 group-hover:text-blue-300 shrink-0">
                <span className="text-sm font-medium">Start Learning</span>
                <ChevronRight className="w-4 h-4" />
              </div>
            </div>

            {/* Personalization Reason (if assessment completed) */}
            {journeyData?.hasAssessment && topic.is_recommended && (
              <div className="mt-2 pt-2 border-t border-gray-600">
                <p className="text-xs text-blue-300">
                  {getPersonalizationReason(topic, journeyData.weaknesses)}
                </p>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Bottom CTA for Assessment */}
      {!journeyData?.hasAssessment && (
        <div className="mt-8 text-center bg-gradient-to-r from-blue-900/20 to-purple-900/20 border border-blue-800/50 rounded-lg p-6">
          <Shield className="w-8 h-8 mx-auto text-blue-400 mb-3" />
          <h3 className="text-lg font-semibold text-white mb-2">
            Want Personalized Learning?
          </h3>
          <p className="text-gray-400 mb-4">
            Take our AI-powered assessment to get personalized topic recommendations based on your skill level and learning goals.
          </p>
          <button 
            onClick={() => window.location.href = '/assessment'}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors"
          >
            Take Assessment
          </button>
        </div>
      )}
    </div>
  );
};

export default LearningJourneyDashboard;